import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import useSetProposalLineItemQuantity from '@/api/mutations/proposals/useSetProposalLineItemQuantity';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import {
  GetProposalQuery,
  SetProposalLineItemQuantityInput,
  SetProposalLineItemQuantityMutation,
} from '@/gql/graphql';
import { useDebounceCallback } from '@payaca/hooks';

import { applyProposalLineItemQuantity } from './applyProposalLineItemQuantity';
import { createOptimisticProposalMutationOptions } from './createOptimisticProposalMutation';

/** Rapid quantity edits (typing digits, holding a +/- button) collapse into a
 * single server write once the user pauses for this long. */
const QUANTITY_WRITE_DEBOUNCE_MS = 500;

/**
 * Optimistic wrapper around {@link useSetProposalLineItemQuantity}. Each call
 * applies the new quantity to the cached proposal immediately so the input
 * stays responsive while typing, but the server write is debounced.
 */
const useOptimisticSetProposalLineItemQuantity = (args: {
  proposalId: string;
  options?: UseMutationOptions<
    SetProposalLineItemQuantityMutation,
    unknown,
    SetProposalLineItemQuantityInput
  >;
}) => {
  const { proposalId, options } = args;
  const queryClient = useQueryClient();
  const queryKey = proposalKeys.proposal(proposalId);

  const mutation = useSetProposalLineItemQuantity({
    proposalId,
    options: {
      ...options,
      ...createOptimisticProposalMutationOptions({
        queryClient,
        queryKey,
        apply: applyProposalLineItemQuantity,
        options,
      }),
    },
  });

  const { mutate } = mutation;

  // Flush on unmount so a pending edit isn't lost if the row unmounts mid-write.
  const debouncedMutate = useDebounceCallback(
    mutate,
    QUANTITY_WRITE_DEBOUNCE_MS,
    { flushOnUnmount: true }
  );

  const setQuantity = useCallback(
    (input: SetProposalLineItemQuantityInput) => {
      // Apply to the cache on every keystroke so the input reflects the change
      // immediately — NumberInput resets its display to the cached value
      // otherwise. The debounced mutation then reconciles with the server.
      queryClient.setQueryData<GetProposalQuery>(
        proposalKeys.proposal(proposalId),
        (old) => (old ? applyProposalLineItemQuantity(old, input) : old)
      );
      debouncedMutate(input);
    },
    [queryClient, proposalId, debouncedMutate]
  );

  return { ...mutation, mutate: setQuantity };
};

export default useOptimisticSetProposalLineItemQuantity;
